/**
 * SLAIndicator Component
 *
 * Visual indicator for SLA status with time remaining display.
 * Shows first response and resolution deadlines with color-coded urgency.
 *
 * Epic: MVP 1.21.1-TKT - Support Ticket System (SCRUM-440)
 * Week 2: Ticket Detail Page Components
 * Location: apps/web/src/components/support/SLAIndicator.jsx
 */

import React from 'react';
import { Clock, CheckCircle, AlertCircle } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import Alert from '../common/Alert';

export function SLAIndicator({ slaStatus }) {
  if (!slaStatus || !slaStatus.hasPolicy) {
    return null;
  }

  const getUrgencyColor = (isBreached, minutes) => {
    if (isBreached) {
      return {
        bg: 'bg-red-50',
        border: 'border-red-300',
        text: 'text-red-700',
        icon: 'text-red-600',
      };
    }

    // Within 30 minutes - critical
    if (minutes <= 30) {
      return {
        bg: 'bg-orange-50',
        border: 'border-orange-300',
        text: 'text-orange-700',
        icon: 'text-orange-600',
      };
    }

    // Within 60 minutes - warning
    if (minutes <= 60) {
      return {
        bg: 'bg-yellow-50',
        border: 'border-yellow-300',
        text: 'text-yellow-700',
        icon: 'text-yellow-600',
      };
    }

    // More than 60 minutes - good
    return {
      bg: 'bg-green-50',
      border: 'border-green-300',
      text: 'text-green-700',
      icon: 'text-green-600',
    };
  };

  const getStatusIcon = (status, isBreached) => {
    if (status === 'met') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    if (status === 'breached' || isBreached) {
      return <AlertCircle className="w-5 h-5 text-red-600" />;
    }
    return <Clock className="w-5 h-5" />;
  };

  const { firstResponse, resolution, policyName, totalBreachCount } = slaStatus;

  return (
    <div className="bg-white rounded-lg shadow border-l-4 border-blue-500">
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">SLA Status</h3>
          </div>
          {totalBreachCount > 0 && (
            <StatusBadge variant="error">
              {totalBreachCount} {totalBreachCount === 1 ? 'Breach' : 'Breaches'}
            </StatusBadge>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">{policyName}</p>

        <div className="space-y-3">
          {/* First Response SLA */}
          <div
            className={`p-4 rounded-lg border ${
              firstResponse.status === 'met'
                ? 'bg-green-50 border-green-300'
                : firstResponse.breached
                ? 'bg-red-50 border-red-300'
                : getUrgencyColor(false, firstResponse.timeRemaining?.minutes || 0).bg + ' ' + getUrgencyColor(false, firstResponse.timeRemaining?.minutes || 0).border
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(firstResponse.status, firstResponse.breached)}
                <span className="text-sm font-semibold text-gray-900">First Response</span>
              </div>
              {firstResponse.status === 'met' && (
                <StatusBadge variant="success">Met</StatusBadge>
              )}
              {firstResponse.breached && (
                <StatusBadge variant="error">Breached</StatusBadge>
              )}
            </div>

            {firstResponse.status === 'pending' && firstResponse.timeRemaining && (
              <div className="flex items-center gap-2">
                {firstResponse.breached ? (
                  <AlertCircle className={`w-4 h-4 ${getUrgencyColor(firstResponse.breached, 0).icon}`} />
                ) : (
                  <Clock className={`w-4 h-4 ${getUrgencyColor(false, firstResponse.timeRemaining.minutes || 0).icon}`} />
                )}
                <span className={`text-sm font-medium ${
                  firstResponse.breached
                    ? 'text-red-700'
                    : getUrgencyColor(false, firstResponse.timeRemaining.minutes || 0).text
                }`}>
                  {firstResponse.timeRemaining.timeRemaining}
                </span>
              </div>
            )}

            {firstResponse.completedAt && (
              <p className="text-xs text-gray-600 mt-1">
                Responded at {new Date(firstResponse.completedAt).toLocaleString()}
              </p>
            )}
          </div>

          {/* Resolution SLA */}
          <div
            className={`p-4 rounded-lg border ${
              resolution.status === 'met'
                ? 'bg-green-50 border-green-300'
                : resolution.breached
                ? 'bg-red-50 border-red-300'
                : getUrgencyColor(false, resolution.timeRemaining?.minutes || 0).bg + ' ' + getUrgencyColor(false, resolution.timeRemaining?.minutes || 0).border
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {getStatusIcon(resolution.status, resolution.breached)}
                <span className="text-sm font-semibold text-gray-900">Resolution</span>
              </div>
              {resolution.status === 'met' && (
                <StatusBadge variant="success">Met</StatusBadge>
              )}
              {resolution.breached && (
                <StatusBadge variant="error">Breached</StatusBadge>
              )}
            </div>

            {resolution.status === 'pending' && resolution.timeRemaining && (
              <div className="flex items-center gap-2">
                {resolution.breached ? (
                  <AlertCircle className={`w-4 h-4 ${getUrgencyColor(resolution.breached, 0).icon}`} />
                ) : (
                  <Clock className={`w-4 h-4 ${getUrgencyColor(false, resolution.timeRemaining.minutes || 0).icon}`} />
                )}
                <span className={`text-sm font-medium ${
                  resolution.breached
                    ? 'text-red-700'
                    : getUrgencyColor(false, resolution.timeRemaining.minutes || 0).text
                }`}>
                  {resolution.timeRemaining.timeRemaining}
                </span>
              </div>
            )}

            {resolution.completedAt && (
              <p className="text-xs text-gray-600 mt-1">
                Resolved at {new Date(resolution.completedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>

        {/* Warning for approaching breach */}
        {(firstResponse.status === 'pending' &&
          firstResponse.timeRemaining &&
          firstResponse.timeRemaining.minutes <= 60 &&
          !firstResponse.breached) && (
          <Alert variant="warning" className="mt-4 p-3">
            <p className="text-xs">
              First response deadline approaching. Please respond soon to avoid SLA breach.
            </p>
          </Alert>
        )}
      </div>
    </div>
  );
}

export default SLAIndicator;
